import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlans,
  getSubscriptionStatus,
  subscribe,
  changeTier,
  cancelSubscription,
  redeemCoupon,
} from '../services/api';
import type {
  PlansResponse,
  SubscriptionInfo,
  SubscribeResponse,
  ChangeTierResponse,
  CancelResponse,
  CouponRedeemResponse,
  Tier,
} from '../types';

/**
 * T044: useSubscription hook — fetch status, subscribe, changeTier, cancel.
 */

/**
 * Redirect to an external URL from within a Shopify embedded app iframe.
 *
 * Uses `window.open(url, '_top')` to navigate the top-level frame, which
 * reliably breaks out of the Shopify Admin iframe in all browsers.
 *
 * Note: `shopify.open()` from App Bridge v4 is designed for opening admin
 * resources (products, orders, etc.) — it silently no-ops for external URLs
 * such as billing confirmation pages.
 */
function redirectToExternal(url: string): void {
  window.open(url, '_top');
}

/** Fetch current subscription status */
export function useSubscriptionStatus() {
  return useQuery<SubscriptionInfo>({
    queryKey: ['subscription', 'status'],
    queryFn: getSubscriptionStatus,
    staleTime: 60_000,
  });
}

/** Fetch available plans */
export function usePlans() {
  return useQuery<PlansResponse>({
    queryKey: ['billing', 'plans'],
    queryFn: getPlans,
    staleTime: 300_000,
  });
}

/** Subscribe to a paid tier */
export function useSubscribe() {
  const queryClient = useQueryClient();

  return useMutation<SubscribeResponse, Error, { tier: Tier; interval?: 'monthly' | 'annual' }>({
    mutationFn: ({ tier, interval }) => subscribe({ tier, interval }),
    onSuccess: (data) => {
      // Redirect merchant to Shopify's charge confirmation page
      if (data.confirmationUrl) {
        redirectToExternal(data.confirmationUrl);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

/** Change tier (upgrade/downgrade) */
export function useChangeTier() {
  const queryClient = useQueryClient();

  return useMutation<ChangeTierResponse, Error, { tier: Tier; interval?: 'monthly' | 'annual' }>({
    mutationFn: ({ tier, interval }) => changeTier({ tier, interval }),
    onSuccess: (data) => {
      if (data.confirmationUrl) {
        redirectToExternal(data.confirmationUrl);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

/** Cancel subscription */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation<CancelResponse, Error, void>({
    mutationFn: () => cancelSubscription(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

/** Redeem a coupon code */
export function useRedeemCoupon() {
  const queryClient = useQueryClient();

  return useMutation<CouponRedeemResponse, Error, string>({
    mutationFn: (code: string) => redeemCoupon({ code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}
